<!-- eslint-disable vue/no-v-html -->
<template>
  <MLayoutStack
    :class="{
      'm-consultation-template-form-element--continuation': isContinuationCheckbox,
      'm-consultation-template-form-element--orphan-checkbox': isOrphanCheckbox,
    }"
    @focusin="handleOnFocus"
  >
    <component
      :is="element.componentName"
      v-bind="element.props"
      :model-value="element.modelValueKey ? formValues[element.modelValueKey] : null"
      :patient-id="patientId"
      :consultation-topic-id="consultationTopicId"
      @update:model-value="handleValueUpdate"
      @blur="handleOnBlur"
    >
      <template v-if="props.element.children">
        <MConsultationTemplateFormElement
          v-for="(item, index) in props.element.children"
          :key="'element' + index"
          :element="item"
          :previous-element="index > 0 ? props.element.children[index - 1] : undefined"
          :form-values="formValues"
          :patient-id="patientId"
          :consultation-topic-id="consultationTopicId"
          @form-value-update="handleFormValueUpdate"
          @field-focused="(p) => emit('fieldFocused', p)"
          @field-blurred="(p) => emit('fieldBlurred', p)"
        />
      </template>
      <template v-if="element.content">
        {{ element.content }}
      </template>
      <span
        v-if="element.markdown"
        v-html="marked.parse(element.markdown, { breaks: true, gfm: true, renderer })"
      ></span>
    </component>
  </MLayoutStack>
</template>

<script setup lang="ts">
import MConsultationTemplateFormElement from "./MConsultationTemplateFormElement.vue";
import MLayoutStack from "../../MLayoutStack/MLayoutStack.vue";
import { marked } from "marked";
import { computed, defineEmits } from "vue";
const props = defineProps({
  element: Object,
  previousElement: Object,
  formValues: Object,
  patientId: String,
  consultationTopicId: String,
});

// A `<checkbox>` schema entry renders as a bare MInputGroup/MCheckbox pair (labelled
// or not — e.g. "Yes" then "No" in a Yes/No question, where only "Yes" carries the
// question's title as its legend).
function isCheckboxGroup(el?: Record<string, unknown> | null) {
  const children = el?.children as Array<{ componentName?: string }> | undefined;
  return (
    el?.componentName === "MInputGroup" &&
    children?.length === 1 &&
    children[0]?.componentName === "MCheckbox"
  );
}

const isUnlabelledCheckbox = computed(
  () => isCheckboxGroup(props.element) && !(props.element?.props as { label?: string })?.label,
);

// Only give the label-less checkbox the same tight spacing as the row above when that
// row is itself a checkbox (a genuine Yes/No continuation). A label-less checkbox that
// follows an unrelated field (e.g. a heading) isn't part of that group, so pull it away
// with extra space instead — otherwise it visually reads as belonging to the row above.
const isContinuationCheckbox = computed(
  () => isUnlabelledCheckbox.value && isCheckboxGroup(props.previousElement),
);
const isOrphanCheckbox = computed(
  () => isUnlabelledCheckbox.value && !isCheckboxGroup(props.previousElement),
);

const emit = defineEmits<{
  (e: "formValueUpdate", payload?: { modelValueKey: string; value: unknown; label?: string }): void;
  (e: "fieldFocused", payload: { modelValueKey: string; value: unknown; label?: string }): void;
  (e: "fieldBlurred", payload: { modelValueKey: string; value: unknown; label?: string }): void;
}>();

const renderer = new marked.Renderer();
const linkRenderer = renderer.link;
renderer.link = (href, title, text) => {
  const html = linkRenderer.call(renderer, href, title, text);
  return html.replace(/^<a /, '<a target="_blank" ');
};

function payload() {
  const key = props.element?.modelValueKey;
  if (!key) return undefined;
  const el = props.element;
  const label =
    el?.props?.label ?? el?.label ?? (typeof el?.content === "string" ? el.content : undefined);
  return { modelValueKey: key, value: props.formValues?.[key], label };
}

function handleFormValueUpdate(child?: { modelValueKey: string; value: unknown; label?: string }) {
  emit("formValueUpdate", child ?? payload());
}

function handleOnBlur() {
  if (props.element.hasOwnProperty("triggerAction") && props.element.triggerAction == "blur") {
    emit("formValueUpdate", payload());
  }
  const p = payload();
  if (p) emit("fieldBlurred", p);
}
function handleValueUpdate(newValue: unknown) {
  const key = props.element?.modelValueKey;
  if (!key) {
    emit("formValueUpdate", undefined);
    return;
  }
  const el = props.element;
  const label =
    el?.props?.label ?? el?.label ?? (typeof el?.content === "string" ? el.content : undefined);
  emit("formValueUpdate", { modelValueKey: key, value: newValue, label });
}

function handleOnFocus() {
  const p = payload();
  if (p) emit("fieldFocused", p);
}
</script>

<style scoped>
.m-consultation-template-form-element--continuation {
  margin-top: calc(var(--gap-1) - var(--gap-4));
}

.m-consultation-template-form-element--orphan-checkbox {
  margin-top: var(--gap-2);
}
</style>
